import { useState, useEffect } from 'react';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { Calendar as CalendarIcon, X, Sparkles, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
//import { Badge } from '../ui/badge';
import { format } from 'date-fns';
import { PostType, Topic, MediaType } from '../../types';
import { PostTypeToggle } from '../posts/PostTypeToggle';
import { TopicsSection } from '../topics/TopicsSection';
import {
  CREATE_PROJECT_POST,
  UPDATE_PROJECT_POST,
  ADD_PROJECT_POST_TARGET,
  REMOVE_PROJECT_POST_TARGET,
} from '../../graphql/mutations';
import {
  GET_PROJECT_POSTS,
  GET_SOCIAL_PROFILES,
  ADAPT_CONTENT_FOR_PLATFORMS,
} from '../../graphql/queries';
import { GetSocialProfilesResponse } from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';
import { toast } from 'sonner';
import { BookOpen } from 'lucide-react';
import { PlatformIcon } from '../common/PlatformIcon';

interface PostToEdit {
  id: string;
  title: string;
  content: string;
  status: string;
  assignedTo?: string;
  scheduledDate?: string;
  dueDate: string;
  postType?: string;
  topicId?: string;
  topic?: Topic;
  notes?: string;
  targets?: Array<{
    id: string;
    platform: string;
    socialProfileId: string;
  }>;
}

interface TeamMember {
  id: string;
  userId: string;
  role: string;
  user: {
    id: string;
    email: string;
  };
}

interface CreatePostModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  postToEdit?: PostToEdit | null;
  teamMembers?: TeamMember[];
  initialValues?: {
    platforms?: string[];
    content?: string;
    title?: string;
    selectedProfiles?: Record<string, string>; // platform -> profileId mapping
  };
  onSubmit?: (post: {
    title: string;
    content: string;
    platforms: string[];
    status: string;
    assignedTo?: string;
    dueDate: Date | undefined;
    scheduledDate?: Date;
  }) => void;
}

const AVAILABLE_PLATFORMS = [
  'X',
  'LinkedIn',
  'Instagram',
  'Facebook',
  'Threads',
  'BlueSky',
  'YouTube',
  'TikTok',
  'Pinterest',
];

export function CreatePostModal({
  open,
  onClose,
  projectId,
  postToEdit,
  teamMembers = [],
  initialValues,
  onSubmit,
}: CreatePostModalProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [selectedProfiles, setSelectedProfiles] = useState<Record<string, string>>({}); // platform -> profileId
  const [status, setStatus] = useState('draft');
  const [assignedTo, setAssignedTo] = useState<string | undefined>();
  const [dueDate, setDueDate] = useState<Date>();
  const [scheduledDate, setScheduledDate] = useState<Date>();
  const [postType, setPostType] = useState<PostType | ''>('');
  const [topicId, setTopicId] = useState<string | undefined>();
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [showTopicSelector, setShowTopicSelector] = useState(false);
  const [mediaFilters, setMediaFilters] = useState<MediaType[]>([
    'IMAGE',
    'VIDEO',
    'GALLERY',
    'PHOTO',
    'BOOKMARK',
  ]);
  const [notes, setNotes] = useState('');
  const [showContentAdaptationModal, setShowContentAdaptationModal] = useState(false);
  const [contentAdaptationError, setContentAdaptationError] = useState<string | null>(null);

  const isEditMode = !!postToEdit;

  const { accountId, selectedAccount } = useAccount();

  // Get social profiles
  const { data: profilesData } = useQuery<GetSocialProfilesResponse>(GET_SOCIAL_PROFILES, {
    variables: { accountId },
    skip: !accountId,
  });

  // Query to adapt content for platforms
  const [adaptContent, { loading: adaptContentLoading, data: adaptContentData }] = useLazyQuery(
    ADAPT_CONTENT_FOR_PLATFORMS,
    {
      fetchPolicy: 'network-only',
      onCompleted: () => {
        setShowContentAdaptationModal(true);
        setContentAdaptationError(null);
      },
      onError: (error) => {
        setContentAdaptationError(error.message);
        setShowContentAdaptationModal(true);
      },
    }
  );

  const handleRetryAdaptContent = () => {
    setContentAdaptationError(null);
    adaptContent({
      variables: { content },
      fetchPolicy: 'network-only',
    });
  };

  const handleAdaptContent = () => {
    if (!content.trim()) {
      toast.error('Please enter some content first');
      return;
    }
    adaptContent({
      variables: { content },
    });
  };

  // Populate form when editing
  useEffect(() => {
    if (postToEdit && open) {
      setTitle(postToEdit.title || '');
      setContent(postToEdit.content || '');
      setStatus(postToEdit.status.toLowerCase().replace(/_/g, '-'));
      setAssignedTo(postToEdit.assignedTo || undefined);
      setDueDate(postToEdit.dueDate ? new Date(postToEdit.dueDate) : undefined);
      setScheduledDate(postToEdit.scheduledDate ? new Date(postToEdit.scheduledDate) : undefined);
      setPostType((postToEdit.postType as PostType) || '');
      setTopicId(postToEdit.topicId || undefined);
      setSelectedTopic(postToEdit.topic || null);
      setNotes(postToEdit.notes || '');

      // Set platforms and selected profiles from targets
      if (postToEdit.targets && postToEdit.targets.length > 0) {
        const targetPlatforms = postToEdit.targets.map((t) => t.platform);
        setPlatforms(targetPlatforms);

        const profiles: Record<string, string> = {};
        postToEdit.targets.forEach((target) => {
          profiles[target.platform] = target.socialProfileId;
        });
        setSelectedProfiles(profiles);
      } else {
        setPlatforms([]);
        setSelectedProfiles({});
      }
    } else if (!postToEdit && open) {
      // Reset form for create mode or apply initial values
      if (initialValues) {
        setTitle(initialValues.title || '');
        setContent(initialValues.content || '');
        setPlatforms(initialValues.platforms || []);
        // Use provided selectedProfiles or set empty to be filled by next effect
        setSelectedProfiles(initialValues.selectedProfiles || {});
      } else {
        setTitle('');
        setContent('');
        setPlatforms([]);
        setSelectedProfiles({});
      }
      setStatus('draft');
      setAssignedTo(undefined);
      setDueDate(undefined);
      setScheduledDate(undefined);
      setPostType('');
      setTopicId(undefined);
      setSelectedTopic(null);
      setNotes('');
    }
  }, [postToEdit, open, initialValues]);

  // Set selected profiles when platforms are set from initial values and profiles are loaded
  // This runs after the form is reset and profiles are loaded
  useEffect(() => {
    if (
      !postToEdit &&
      open &&
      initialValues?.platforms &&
      initialValues.platforms.length > 0 &&
      profilesData?.socialProfiles &&
      (!initialValues.selectedProfiles || Object.keys(initialValues.selectedProfiles).length === 0)
    ) {
      // Only auto-select if profiles weren't provided in initialValues
      const currentProfiles = initialValues.selectedProfiles || {};
      const profiles: Record<string, string> = { ...currentProfiles };

      initialValues.platforms.forEach((platform) => {
        // Only set if not already set
        if (!profiles[platform]) {
          // Find first available profile for this platform
          const availableProfile = profilesData.socialProfiles.find((p) => p.platform === platform);
          if (availableProfile) {
            profiles[platform] = availableProfile.id;
          }
        }
      });

      // Only update if we found new profiles
      if (Object.keys(profiles).length > Object.keys(currentProfiles).length) {
        setSelectedProfiles(profiles);
      }
    }
  }, [initialValues, profilesData, open, postToEdit]);

  const [createProjectPost, { loading: creatingPost }] = useMutation(CREATE_PROJECT_POST, {
    refetchQueries: [{ query: GET_PROJECT_POSTS, variables: { projectId } }],
    onCompleted: async (data) => {
      const postId = data.createProjectPost.id;

      // Add targets for each selected platform/profile
      const targetPromises = Object.entries(selectedProfiles).map(([platform, profileId]) =>
        addTarget({
          variables: {
            input: {
              projectPostId: postId,
              socialProfileId: profileId,
              platform,
            },
          },
        })
      );

      try {
        await Promise.all(targetPromises);
        toast.success('Post created successfully!');
        handleClose();
        if (onSubmit) {
          onSubmit({
            title,
            content,
            platforms,
            status,
            assignedTo,
            dueDate,
            scheduledDate,
          });
        }
      } catch (error: any) {
        toast.error(`Failed to add targets: ${error.message}`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to create post: ${error.message}`);
    },
  });

  const [updateProjectPost, { loading: updatingPost }] = useMutation(UPDATE_PROJECT_POST, {
    refetchQueries: [{ query: GET_PROJECT_POSTS, variables: { projectId } }],
    onCompleted: async () => {
      if (!postToEdit) return;

      // Get current targets
      const currentTargets = postToEdit.targets || [];
      const currentTargetMap = new Map(
        currentTargets.map((t) => [`${t.platform}:${t.socialProfileId}`, t.id])
      );

      // Determine which targets to remove and which to add
      const newTargetKeys = new Set(
        Object.entries(selectedProfiles).map(([platform, profileId]) => `${platform}:${profileId}`)
      );

      // Remove targets that are no longer selected
      const targetsToRemove: string[] = [];
      currentTargets.forEach((target) => {
        const key = `${target.platform}:${target.socialProfileId}`;
        if (!newTargetKeys.has(key)) {
          targetsToRemove.push(target.id);
        }
      });

      // Add new targets
      const targetsToAdd: Array<{ platform: string; profileId: string }> = [];
      Object.entries(selectedProfiles).forEach(([platform, profileId]) => {
        const key = `${platform}:${profileId}`;
        if (!currentTargetMap.has(key)) {
          targetsToAdd.push({ platform, profileId });
        }
      });

      try {
        // Remove old targets
        const removePromises = targetsToRemove.map((targetId) =>
          removeTarget({
            variables: { id: targetId },
          })
        );

        // Add new targets
        const addPromises = targetsToAdd.map(({ platform, profileId }) =>
          addTarget({
            variables: {
              input: {
                projectPostId: postToEdit.id,
                socialProfileId: profileId,
                platform,
              },
            },
          })
        );

        await Promise.all([...removePromises, ...addPromises]);
        toast.success('Post updated successfully!');
        handleClose();
        if (onSubmit) {
          onSubmit({
            title,
            content,
            platforms,
            status,
            assignedTo,
            dueDate,
            scheduledDate,
          });
        }
      } catch (error: any) {
        toast.error(`Failed to update targets: ${error.message}`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to update post: ${error.message}`);
    },
  });

  const [addTarget, { loading: addingTarget }] = useMutation(ADD_PROJECT_POST_TARGET);
  const [removeTarget, { loading: removingTarget }] = useMutation(REMOVE_PROJECT_POST_TARGET);

  const handleSubmit = () => {
    if (!accountId || !dueDate || platforms.length === 0) return;

    // Validate that each platform has a profile selected
    const missingProfiles = platforms.filter((p) => !selectedProfiles[p]);
    if (missingProfiles.length > 0) {
      toast.error(`Please select profiles for: ${missingProfiles.join(', ')}`);
      return;
    }

    if (isEditMode && postToEdit) {
      updateProjectPost({
        variables: {
          id: postToEdit.id,
          input: {
            title,
            content,
            status: status.toLowerCase().replace(/-/g, '_'),
            assignedTo: assignedTo && assignedTo !== 'unassigned' ? assignedTo : undefined,
            scheduledDate: scheduledDate ? scheduledDate.toISOString() : undefined,
            dueDate: dueDate.toISOString(),
            postType: postType || undefined,
            topicId: topicId || undefined,
            notes: notes || undefined,
          },
        },
      });
    } else {
      createProjectPost({
        variables: {
          input: {
            projectId,
            accountId,
            title,
            content,
            status: status.toLowerCase().replace(/-/g, '_'),
            assignedTo: assignedTo && assignedTo !== 'unassigned' ? assignedTo : undefined,
            scheduledDate: scheduledDate ? scheduledDate.toISOString() : undefined,
            dueDate: dueDate.toISOString(),
            postType: postType || undefined,
            topicId: topicId || undefined,
            notes: notes || undefined,
          },
        },
      });
    }
  };

  const handleClose = () => {
    setTitle('');
    setContent('');
    setPlatforms([]);
    setSelectedProfiles({});
    setStatus('draft');
    setAssignedTo(undefined);
    setDueDate(undefined);
    setScheduledDate(undefined);
    setPostType('');
    setTopicId(undefined);
    setSelectedTopic(null);
    setNotes('');
    setShowTopicSelector(false);
    onClose();
  };

  const handleTopicSelect = (topic: Topic) => {
    setSelectedTopic(topic);
    setTopicId(topic.id);
    setShowTopicSelector(false);
  };

  const togglePlatform = (platform: string) => {
    setPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="!max-w-[90vw] sm:!max-w-[85vw] md:!max-w-[75vw] lg:!max-w-[70vw] xl:!max-w-6xl max-h-[90vh] overflow-y-auto bg-core-dark text-white">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Post' : 'Create New Post'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update your social media post. You can modify the content, platforms, schedule, and assignment.'
              : 'Add a new social media post to your project. You can schedule it and assign it to team members.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 py-4">
          {/* Left Column - Main Fields (3/5 width) */}
          <div className="lg:col-span-3 space-y-6">
            {/* Post Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Product Teaser #1"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="bg-form-dark text-cmp-ink"
              />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="content">Text *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAdaptContent}
                  disabled={adaptContentLoading || !content.trim()}
                  className="gap-2 h-6 flex-shrink-0 text-xs"
                >
                  {adaptContentLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Thinking...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Try AI Suggestions
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                id="content"
                placeholder="Write your post content here..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="min-h-32 resize-none bg-form-dark text-cmp-ink"
              />
              <p className="text-cmp-ink-3">{content.length} characters</p>
            </div>

            {/* Post Type */}
            <div className="space-y-2">
              <Label>Post Type (Optional)</Label>
              <PostTypeToggle value={postType} onChange={(_, newValue) => setPostType(newValue)} />
              <p className="text-sm text-cmp-ink-3">
                Select the type of post you want to create. Leave empty to let the system decide.
              </p>
            </div>

            {/* Topic Selection */}
            <div className="space-y-2">
              <Label>Topic (Optional)</Label>
              {selectedTopic ? (
                <div className="flex items-center justify-between p-3 bg-form-dark rounded-lg border border-cmp-line-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">
                      {selectedTopic.title || 'Untitled Topic'}
                    </p>
                    {selectedTopic.summary && (
                      <p className="text-xs text-cmp-ink-2 mt-1 line-clamp-2">
                        {selectedTopic.summary.replace(/<[^>]*>/g, '').substring(0, 100)}
                        {selectedTopic.summary.length > 100 ? '...' : ''}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedTopic(null);
                      setTopicId(undefined);
                    }}
                    className="ml-2 text-cmp-ink-2 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowTopicSelector(true)}
                  className="w-full justify-start bg-form-dark text-cmp-ink border-cmp-line-2 hover:bg-cmp-bronze-soft"
                >
                  <BookOpen className="mr-2 h-4 w-4" />
                  Select a topic from media library
                </Button>
              )}
              <p className="text-sm text-cmp-ink-3">
                Link this post to a topic from your media library. This helps organize and track
                content.
              </p>
            </div>

            {/* Platforms */}
            <div className="space-y-2">
              <Label>Target Platforms *</Label>
              <div className="flex flex-wrap gap-2 p-4 bg-form-dark rounded-lg">
                {AVAILABLE_PLATFORMS.map((platform) => {
                  const isSelected = platforms.includes(platform);
                  return (
                    <Button
                      key={platform}
                      variant={isSelected ? 'default' : 'outline'}
                      size="default"
                      onClick={() => togglePlatform(platform)}
                      className={isSelected ? '' : ''}
                    >
                      {platform}
                      {isSelected && <X className="ml-2 w-3 h-3" />}
                    </Button>
                  );
                })}
              </div>
              {platforms.length > 0 && (
                <p className="text-cmp-ink-3">{platforms.length} platform(s) selected</p>
              )}
            </div>

            {/* Profile Selection for Each Platform */}
            {platforms.length > 0 && profilesData && (
              <div className="space-y-4">
                <Label>Select Profiles for Each Platform *</Label>
                {platforms.map((platform) => {
                  const availableProfiles = profilesData.socialProfiles.filter(
                    (p) => p.platform.toLowerCase() === platform.toLowerCase() && p.isActive
                  );
                  return (
                    <div key={platform} className="space-y-2">
                      <Label className="text-sm">{platform}</Label>
                      <Select
                        value={selectedProfiles[platform] || ''}
                        onValueChange={(value) =>
                          setSelectedProfiles({ ...selectedProfiles, [platform]: value })
                        }
                        disabled={availableProfiles.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              availableProfiles.length === 0
                                ? `No ${platform} profiles available`
                                : `Select ${platform} profile`
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {availableProfiles.map((profile) => (
                            <SelectItem key={profile.id} value={profile.id}>
                              {profile.profileName || profile.profileUsername || profile.id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {availableProfiles.length === 0 && (
                        <p className="text-sm text-cmp-ink-3">
                          No {platform} profiles available. Please connect a {platform} account
                          first.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Status & Assignment Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="in-review">In Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Assign To</Label>
                <Select
                  value={assignedTo || 'unassigned'}
                  onValueChange={(value) =>
                    setAssignedTo(value === 'unassigned' ? undefined : value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {teamMembers.map((member) => (
                      <SelectItem key={member.user.id} value={member.user.id}>
                        {member.user.email} {member.role ? `(${member.role})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Due Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-form-dark text-cmp-ink"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dueDate ? format(dueDate, 'PPP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="!w-fit p-2 bg-core-dark" align="start">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={setDueDate}
                      initialFocus
                      className="p-2"
                      classNames={{
                        cell: 'flex-shrink-0 w-9 px-0.5',
                        day: 'flex-shrink-0 w-9 h-9',
                        head_cell: 'flex-shrink-0 w-9 px-0.5',
                        row: 'gap-0.5',
                        table: 'gap-0',
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Scheduled Date (Optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-form-dark text-cmp-ink"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {scheduledDate ? format(scheduledDate, 'PPP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="!w-fit p-2 bg-core-dark" align="start">
                    <Calendar
                      mode="single"
                      selected={scheduledDate}
                      onSelect={setScheduledDate}
                      initialFocus
                      className="p-2"
                      classNames={{
                        cell: 'flex-shrink-0 w-9 px-0.5',
                        day: 'flex-shrink-0 w-9 h-9',
                        head_cell: 'flex-shrink-0 w-9 px-0.5',
                        row: 'gap-0.5',
                        table: 'gap-0',
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          {/* Right Column - Notes (2/5 width) */}
          <div className="lg:col-span-2 space-y-6">
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional notes about this post..."
                className="min-h-[calc(100vh-400px)] resize-none bg-form-dark text-cmp-ink"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !title ||
              !content ||
              platforms.length === 0 ||
              !dueDate ||
              creatingPost ||
              updatingPost ||
              addingTarget ||
              removingTarget ||
              platforms.some((p) => !selectedProfiles[p])
            }
          >
            {creatingPost || updatingPost || addingTarget || removingTarget
              ? isEditMode
                ? 'Updating...'
                : 'Creating...'
              : isEditMode
                ? 'Update Post'
                : 'Create Post'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Topic Selector Dialog */}
      {showTopicSelector && accountId && (
        <Dialog open={showTopicSelector} onOpenChange={setShowTopicSelector}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-core-dark text-white">
            <DialogHeader>
              <DialogTitle>Select Topic from Media Library</DialogTitle>
              <DialogDescription>
                Choose a topic to link to this post. You can filter by media type.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <TopicsSection
                accountId={accountId}
                onTopicSelect={handleTopicSelect}
                mediaFilters={mediaFilters}
                onMediaFilterChange={(event, newFilters) => {
                  setMediaFilters(newFilters);
                }}
                selectedAccount={selectedAccount || null}
                onRefetchReady={() => {}}
                clickBehavior="select"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* AI Content Adaptation Modal */}
      <Dialog open={showContentAdaptationModal} onOpenChange={setShowContentAdaptationModal}>
        <DialogContent className="bg-core-dark border-cmp-line-2 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">AI Suggestions</DialogTitle>
            <DialogDescription className="text-cmp-ink-2">
              Choose an AI suggestion or keep your original content
            </DialogDescription>
          </DialogHeader>

          {contentAdaptationError ? (
            <div className="space-y-4">
              <div className="p-4 bg-red-500/20 border border-red-500 rounded text-red-400">
                {contentAdaptationError}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowContentAdaptationModal(false)}
                  className="bg-form-dark text-cmp-ink"
                >
                  Close
                </Button>
                <Button
                  type="button"
                  onClick={handleRetryAdaptContent}
                  disabled={adaptContentLoading}
                >
                  {adaptContentLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Try Again
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : adaptContentLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
              <span className="ml-3 text-white">Thinking...</span>
            </div>
          ) : adaptContentData?.adaptContentForPlatforms?.platforms ? (
            <div className="space-y-4">
              {/* Original Content */}
              <div className="p-4 bg-form-dark rounded-lg border border-cmp-line-2">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-white font-semibold">Original Content</Label>
                  <span className="text-sm text-cmp-ink-2">{content.length} characters</span>
                </div>
                <p className="text-cmp-ink whitespace-pre-wrap">{content}</p>
              </div>

              {/* Platform Suggestions */}
              <div className="space-y-3">
                <Label className="text-white font-semibold">Platform-Specific Versions</Label>
                {adaptContentData.adaptContentForPlatforms.platforms.map((platformContent: any) => (
                  <div
                    key={platformContent.platform}
                    className="p-4 bg-form-dark rounded-lg border border-cmp-line-2 hover:border-pink-500 transition-colors cursor-pointer"
                    onClick={() => {
                      setContent(platformContent.content);
                      setShowContentAdaptationModal(false);
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <PlatformIcon platform={platformContent.platform} size={20} />
                        <span className="text-white font-medium capitalize">
                          {platformContent.platform}
                        </span>
                        {platformContent.isTruncated && (
                          <span className="text-xs text-yellow-400">(Truncated)</span>
                        )}
                      </div>
                      <span className="text-sm text-cmp-ink-2">
                        {platformContent.characterCount} characters
                      </span>
                    </div>
                    <p className="text-cmp-ink whitespace-pre-wrap">{platformContent.content}</p>
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowContentAdaptationModal(false)}
                  className="bg-form-dark text-cmp-ink"
                >
                  Keep Original
                </Button>
                <Button
                  type="button"
                  onClick={handleRetryAdaptContent}
                  disabled={adaptContentLoading}
                  variant="outline"
                  className="bg-form-dark text-cmp-ink"
                >
                  {adaptContentLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Try Again
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
